import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HandHeart, HeartHandshake, Building2, Megaphone } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CTASection } from "../components/CTASection";
import { Reveal } from "../components/Reveal";
import { PROGRAMS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAYS = [
  { icon: HandHeart, title: "Volunteer", text: "Join our camps, awareness drives and community programs as an on-ground volunteer." },
  { icon: HeartHandshake, title: "Donate", text: "Support health camps, eye screenings and skill training with a one-time or monthly gift." },
  { icon: Building2, title: "Partner With Us", text: "Hospitals, schools, corporates and institutions can partner on programs and CSR initiatives." },
  { icon: Megaphone, title: "Spread the Word", text: "Help us reach more families by sharing our work within your networks and community." },
];

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-hecso-ink focus:outline-none focus:ring-2 focus:ring-hecso-green focus:border-hecso-green transition";

export default function GetInvolved() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", interest: "", availability: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteer`, form);
      toast.success("Thank you for volunteering! Our team will reach out to you soon.");
      setForm({ name: "", email: "", phone: "", city: "", interest: "", availability: "", message: "" });
    } catch {
      toast.error("Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="get-involved-page">
      <PageHeader
        title="Get Involved"
        subtitle="Whether you give your time, skills or resources — you can help build healthier communities in Assam."
      />

      <section className="py-16 sm:py-20 bg-hecso-beige">
        <div className="container-hecso grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {WAYS.map((w, i) => (
            <Reveal key={w.title} delay={i * 100}>
              <div className="h-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-center" data-testid={`way-card-${i}`}>
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-hecso-greensubtle text-hecso-green">
                  <w.icon className="w-6 h-6" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-hecso-ink">{w.title}</h3>
                <p className="mt-2 text-sm text-hecso-muted leading-relaxed">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="volunteer-section">
        <div className="container-hecso max-w-3xl">
          <Reveal className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-hecso-orange">Volunteer Application</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-hecso-ink">Become a HECSO Volunteer</h2>
            <p className="mt-3 text-sm sm:text-base text-hecso-muted leading-relaxed">
              Fill in the form below and our volunteer coordinator will contact you about upcoming camps and programs.
            </p>
          </Reveal>
          <Reveal>
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-hecso-beige p-6 sm:p-8 shadow-sm space-y-5" data-testid="volunteer-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Full Name *</label>
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="volunteer-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Email *</label>
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="volunteer-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Phone *</label>
                  <input required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 XXXXX XXXXX" data-testid="volunteer-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">City / District</label>
                  <input value={form.city} onChange={set("city")} className={inputCls} placeholder="e.g. Guwahati, Nalbari" data-testid="volunteer-city-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Area of Interest *</label>
                  <select required value={form.interest} onChange={set("interest")} className={inputCls} data-testid="volunteer-interest-select">
                    <option value="">Select a program area</option>
                    {PROGRAMS.map((p) => (
                      <option key={p.slug} value={p.title}>{p.title}</option>
                    ))}
                    <option value="General Volunteering">General Volunteering</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Availability</label>
                  <select value={form.availability} onChange={set("availability")} className={inputCls} data-testid="volunteer-availability-select">
                    <option value="">Select availability</option>
                    <option>Weekends</option>
                    <option>Weekdays</option>
                    <option>Flexible</option>
                    <option>Events only</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-hecso-ink mb-1.5">Tell us about yourself</label>
                <textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Your skills, experience or why you'd like to volunteer..." data-testid="volunteer-message-input" />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="volunteer-submit-btn"
                className="w-full sm:w-auto rounded-lg bg-hecso-green px-8 py-3 text-sm font-semibold text-white transition-all hover:bg-hecso-greendark hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
